/*
  Realtime voice session: browser <-> /api/voice/session <-> Gemini Live.

  Capture  PCM16 mono @16 kHz  (binary frames up)
  Playback PCM16 mono @24 kHz  (binary frames down)

  Two details that matter and are easy to get wrong:

  1. Sample rate. We ask for a 16 kHz AudioContext, but browsers are allowed to
     ignore that and hand back the hardware rate (typically 44.1/48 kHz). We
     therefore always resample explicitly rather than trusting the request —
     sending 48 kHz audio labelled as 16 kHz makes the agent hear chipmunks.

  2. Barge-in. Gemini sends `interrupted` when the user talks over it. Queued
     audio must be dropped immediately, otherwise the agent keeps talking for
     seconds after being cut off and the two voices overlap.
*/

const IN_RATE = 16000;
const OUT_RATE = 24000;

// Worklet lives in a blob so there is no extra static asset to deploy or cache-bust.
const CAPTURE_WORKLET = `
class CaptureProcessor extends AudioWorkletProcessor {
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch && ch.length) this.port.postMessage(new Float32Array(ch));
    return true;
  }
}
registerProcessor('capture', CaptureProcessor);
`;

function floatToPcm16(float32) {
  const out = new Int16Array(float32.length);
  for (let i = 0; i < float32.length; i++) {
    const s = Math.max(-1, Math.min(1, float32[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

/* Linear resample. Good enough for speech and dependency-free. */
function resample(float32, from, to) {
  if (from === to) return float32;
  const ratio = from / to;
  const len = Math.round(float32.length / ratio);
  const out = new Float32Array(len);
  for (let i = 0; i < len; i++) {
    const pos = i * ratio;
    const i0 = Math.floor(pos);
    const i1 = Math.min(i0 + 1, float32.length - 1);
    const frac = pos - i0;
    out[i] = float32[i0] * (1 - frac) + float32[i1] * frac;
  }
  return out;
}

export function createVoiceSession({ step = 'signup', path = '', onEvent = () => {} }) {
  let ws = null;
  let inCtx = null;
  let outCtx = null;
  let stream = null;
  let node = null;
  let source = null;
  let closed = false;

  // Playback scheduling: keep a cursor so chunks butt up against each other
  // instead of overlapping or gapping.
  let playAt = 0;
  let sources = [];

  const wsUrl = () => {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}/api/voice/session`;
  };

  const flushPlayback = () => {
    for (const s of sources) { try { s.stop(); } catch { /* already ended */ } }
    sources = [];
    playAt = outCtx ? outCtx.currentTime : 0;
  };

  const playPcm24 = (arrayBuf) => {
    if (!outCtx || closed) return;
    const pcm = new Int16Array(arrayBuf);
    if (!pcm.length) return;
    const buf = outCtx.createBuffer(1, pcm.length, OUT_RATE);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < pcm.length; i++) ch[i] = pcm[i] / 0x8000;
    const src = outCtx.createBufferSource();
    src.buffer = buf;
    src.connect(outCtx.destination);
    const now = outCtx.currentTime;
    if (playAt < now) playAt = now + 0.03;   // small lead-in after a gap
    src.start(playAt);
    playAt += buf.duration;
    sources.push(src);
    src.onended = () => { sources = sources.filter((s) => s !== src); };
  };

  const start = async () => {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
    });

    const Ctx = window.AudioContext || window.webkitAudioContext;
    inCtx = new Ctx({ sampleRate: IN_RATE });
    outCtx = new Ctx({ sampleRate: OUT_RATE });
    // Autoplay policy: contexts can start suspended until a gesture resumes them.
    if (outCtx.state === 'suspended') await outCtx.resume().catch(() => {});
    playAt = outCtx.currentTime;

    ws = new WebSocket(wsUrl());
    ws.binaryType = 'arraybuffer';

    ws.onopen = () => ws.send(JSON.stringify({ type: 'hello', step, path }));
    ws.onmessage = (ev) => {
      if (ev.data instanceof ArrayBuffer) { playPcm24(ev.data); return; }
      let d; try { d = JSON.parse(ev.data); } catch { return; }
      if (d.type === 'interrupted') flushPlayback();
      onEvent(d);
    };
    ws.onerror = () => onEvent({ type: 'error', message: 'connection failed' });
    ws.onclose = () => { if (!closed) onEvent({ type: 'closed' }); };

    source = inCtx.createMediaStreamSource(stream);
    const actualRate = inCtx.sampleRate;   // may not be IN_RATE — resample if so

    const pump = (float32) => {
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      const res = resample(float32, actualRate, IN_RATE);
      const pcm = floatToPcm16(res);
      ws.send(pcm.buffer);
    };

    // Both node types need a downstream connection to be pulled by the graph,
    // but connecting the MIC to destination would play it straight back out of
    // the speakers. Sink it through a muted gain node instead.
    const sink = inCtx.createGain();
    sink.gain.value = 0;
    sink.connect(inCtx.destination);

    try {
      const url = URL.createObjectURL(new Blob([CAPTURE_WORKLET], { type: 'application/javascript' }));
      await inCtx.audioWorklet.addModule(url);
      URL.revokeObjectURL(url);
      node = new AudioWorkletNode(inCtx, 'capture');
      node.port.onmessage = (e) => pump(e.data);
      source.connect(node);
      node.connect(sink);
    } catch {
      // ScriptProcessor is deprecated but still the reliable fallback.
      node = inCtx.createScriptProcessor(4096, 1, 1);
      node.onaudioprocess = (e) => pump(e.inputBuffer.getChannelData(0));
      source.connect(node);
      node.connect(sink);
    }
  };

  const sendText = (text) => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'text', text }));
    }
  };

  const stop = () => {
    closed = true;
    flushPlayback();
    try { node && node.disconnect(); } catch { /* noop */ }
    try { source && source.disconnect(); } catch { /* noop */ }
    if (stream) stream.getTracks().forEach((t) => t.stop());
    try { inCtx && inCtx.close(); } catch { /* noop */ }
    try { outCtx && outCtx.close(); } catch { /* noop */ }
    try { ws && ws.close(); } catch { /* noop */ }
    ws = null; node = null; source = null; stream = null; inCtx = null; outCtx = null;
  };

  return { start, stop, sendText };
}
